import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import { formatAmount } from "../../lib/format-utils";
import { channels } from "../../lib/constants";
import { getReceivableRemainingAmount } from "../../../lib/receivables";
import type { ChannelId, ReceivableJournalEntry, ReceivablesResponse } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

export const ReceivablesView = ({
  houseId,
  search,
  setSearch,
}: {
  houseId: number | null;
  search: string;
  setSearch: (nextSearch: string, options?: { replace?: boolean }) => void;
}) => {
  const { t } = useI18n();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const channelParams = searchParams.getAll("channel");
  const settledParam = searchParams.get("settled") ?? "false";
  const [receivables, setReceivables] = useState<ReceivableJournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const settledSelection = useMemo(() => {
    if (settledParam === "true") {
      return { settled: true, unsettled: false };
    }
    if (settledParam === "false") {
      return { settled: false, unsettled: true };
    }
    return { settled: true, unsettled: true };
  }, [settledParam]);

  const channelOptions = channels.map((channel) => ({
    id: channel.id,
    label: t(`channel.label.${channel.id}`, channel.label),
  }))
    .filter((channel) => channel.id !== "eloverblik");
  const selectedChannelIds = useMemo(() => {
    if (!channelOptions.length) {
      return [] as ChannelId[];
    }
    const validChannels = new Set(channelOptions.map((channel) => channel.id));
    const requested = channelParams.filter((id): id is ChannelId => validChannels.has(id as ChannelId));
    return requested.length > 0 ? requested : [channelOptions[0].id];
  }, [channelOptions, channelParams]);
  const selectedChannelKey = useMemo(
    () => selectedChannelIds.slice().sort().join(","),
    [selectedChannelIds],
  );
  const channelLabelById = useMemo(
    () => new Map(channelOptions.map((channel) => [channel.id, channel.label])),
    [channelOptions],
  );
  const outstandingTotal = receivables.reduce(
    (sum, entry) => sum + getReceivableRemainingAmount(entry.amount, entry.linkedAmount),
    0,
  );
  const outstandingCurrency = receivables.find((entry) => entry.currency)?.currency ?? "DKK";

  const loadReceivables = async () => {
    if (!houseId) {
      setReceivables([]);
      return;
    }
    if (selectedChannelIds.length === 0) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      selectedChannelIds.forEach((channelId) => {
        query.append("channel", channelId);
      });
      if (settledParam) {
        query.set("settled", settledParam);
      }
      const response = await fetchJson<ReceivablesResponse>(
        `/api/houses/${houseId}/receivables?${query.toString()}`,
      );
      setReceivables(response.receivables);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : t("receivables.loadFailed", "Failed to load receivables"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadReceivables();
  }, [houseId, selectedChannelKey, settledParam]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("receivables.title", "Receivables")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("receivables.selectHouse", "Select a house to review receivables.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const updateFilter = (next: { channels?: ChannelId[]; settled?: string }) => {
    const nextParams = new URLSearchParams(search);
    if (next.channels !== undefined) {
      nextParams.delete("channel");
      next.channels.forEach((channelId) => nextParams.append("channel", channelId));
    }
    if (next.settled !== undefined) {
      if (next.settled) {
        nextParams.set("settled", next.settled);
      } else {
        nextParams.delete("settled");
      }
    }
    setSearch(`?${nextParams.toString()}`);
  };

  const toggleChannelFilter = (channelId: ChannelId) => {
    const selected = new Set(selectedChannelIds);
    if (selected.has(channelId)) {
      selected.delete(channelId);
    } else {
      selected.add(channelId);
    }
    if (selected.size === 0) {
      return;
    }
    updateFilter({ channels: Array.from(selected) });
  };

  const toggleSettledFilter = (target: "settled" | "unsettled") => {
    const nextSelection = {
      settled:
        target === "settled" ? !settledSelection.settled : settledSelection.settled,
      unsettled:
        target === "unsettled"
          ? !settledSelection.unsettled
          : settledSelection.unsettled,
    };

    if (!nextSelection.settled && !nextSelection.unsettled) {
      return;
    }

    const nextParam = nextSelection.settled && nextSelection.unsettled
      ? "all"
      : nextSelection.settled
        ? "true"
        : "false";

    updateFilter({ settled: nextParam });
  };

  useEffect(() => {
    if (!channelOptions.length) {
      return;
    }
    const validChannels = new Set(channelOptions.map((channel) => channel.id));
    const selectedFromUrl = channelParams.filter((id): id is ChannelId => validChannels.has(id as ChannelId));
    if (selectedFromUrl.length > 0) {
      return;
    }
    const nextParams = new URLSearchParams(search);
    nextParams.delete("channel");
    nextParams.append("channel", channelOptions[0].id);
    setSearch(`?${nextParams.toString()}`, { replace: true });
  }, [channelOptions, channelParams, search, setSearch]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-lg font-semibold text-ink">{t("receivables.title", "Receivables")}</p>
          <p className="text-sm text-muted">
            {t("receivables.subtitle", "Track channel receivables and settlement.")}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-6">
          <div className="space-y-1">
            <span className="text-sm font-medium text-ink">
              {t("bankTransactions.channel", "Channel")}
            </span>
            <div className="flex flex-wrap gap-2">
              {channelOptions.map((channel) => (
                <Button
                  key={channel.id}
                  type="button"
                  size="lg"
                  variant={selectedChannelIds.includes(channel.id) ? "default" : "outline"}
                  aria-pressed={selectedChannelIds.includes(channel.id)}
                  onClick={() => toggleChannelFilter(channel.id)}
                >
                  {channel.label}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <span className="text-sm font-medium text-ink" id="receivables-status-label">
              {t("receivables.status", "Status")}
            </span>
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-labelledby="receivables-status-label"
            >
              <Button
                type="button"
                size="lg"
                variant={settledSelection.unsettled ? "default" : "outline"}
                aria-pressed={settledSelection.unsettled}
                onClick={() => toggleSettledFilter("unsettled")}
              >
                {t("receivables.unsettled", "Unsettled")}
              </Button>
              <Button
                type="button"
                size="lg"
                variant={settledSelection.settled ? "default" : "outline"}
                aria-pressed={settledSelection.settled}
                onClick={() => toggleSettledFilter("settled")}
              >
                {t("receivables.settled", "Settled")}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted">{t("receivables.loading", "Loading receivables...")}</p>
      ) : null}
      {error ? <p className="text-sm text-error">{error}</p> : null}
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <CardTitle>{t("receivables.listTitle", "Receivables list")}</CardTitle>
          <div className="text-left sm:text-right">
            <p className="text-sm text-muted">
              {t("receivables.outstandingTotal", "Outstanding total")}
            </p>
            <p className="text-xl font-semibold text-ink">
              {formatAmount(outstandingTotal, outstandingCurrency)}
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {receivables.length === 0 ? (
            <p className="text-sm text-muted">
              {t("receivables.empty", "No receivables for the selected filters.")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("receivables.bookingId", "Booking ID")}</TableHead>
                  <TableHead>{t("bankTransactions.channel", "Channel")}</TableHead>
                  <TableHead>{t("entries.account", "Account")}</TableHead>
                  <TableHead>{t("entries.journalDate", "Journal date")}</TableHead>
                  <TableHead className="text-right">{t("bankTransactions.amount", "Amount")}</TableHead>
                  <TableHead>{t("receivables.status", "Status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receivables.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.bookingId ?? "—"}</TableCell>
                    <TableCell>
                      {entry.channel ? (channelLabelById.get(entry.channel) ?? entry.channel) : "—"}
                    </TableCell>
                    <TableCell>
                      {entry.accountLabelKey
                        ? t(entry.accountLabelKey, entry.accountName ?? "")
                        : entry.accountName ?? t("entries.noAccount", "No account")}
                    </TableCell>
                    <TableCell>{entry.journalDate ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      {formatAmount(entry.amount, entry.currency)}
                    </TableCell>
                    <TableCell
                      aria-label={
                        entry.settled
                          ? t("receivables.settled", "Settled")
                          : t("receivables.unsettled", "Unsettled")
                      }
                    >
                      {entry.settled ? "✓" : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
